import axios, { AxiosError } from 'axios';
import { apiRoute } from '../constants/api-constants';
import { GuestGuideBody, GuideOpenBody, GuidePinOk, GuidePreviewStage } from '../interfaces/stay-guide';
import { publicApi } from './public-client';
import { StaysApiError } from './stays-client';
import { StayPublicError } from './stays-public-client';

/**
 * LOBBERO STAYS — the stay guest's GUIDE client (plan S7.7;
 * lobbero_be/src/stays/routes/guest-guide.js). Public: the token in the path
 * is the whole credential, so these ride `publicApi` (withCredentials — the
 * PIN unlock cookie is httpOnly and same-origin; the page only re-asks).
 *
 * Every refusal is a GuidePublicError (a StayPublicError, so the S3/S6 pages'
 * checks still hold) that also carries the PIN fields of the body:
 * `attempts_left` (401 PIN_WRONG) and `locked_until` (423 PIN_LOCKED).
 *
 * NEVER LOGGED, NEVER STORED: the token, the PIN, any access value.
 *
 * `fetchGuidePreview` is the one STAFF call here — the host preview (S7.9)
 * through the default axios instance (axios-setup.ts: session cookies, CSRF,
 * the 401 refresh), exactly like stays-client.ts. The preview never calls a
 * guest route.
 */

export class GuidePublicError extends StayPublicError {
  attemptsLeft: number | null;
  lockedUntil: string | null;

  constructor(status: number, body: Record<string, unknown> | null, fallback: string) {
    super(status, body, fallback);
    this.name = 'GuidePublicError';
    this.attemptsLeft = body && typeof body.attempts_left === 'number' ? body.attempts_left : null;
    this.lockedUntil = body && typeof body.locked_until === 'string' ? body.locked_until : null;
  }
}

const call = async <T>(p: Promise<{ data: T }>, fallback: string): Promise<T> => {
  try {
    const { data } = await p;

    return data;
  } catch (e) {
    const err = e as AxiosError<Record<string, unknown>>;
    if (err && err.response) {
      const body = err.response.data && typeof err.response.data === 'object' ? err.response.data : null;
      throw new GuidePublicError(err.response.status, body, fallback);
    }
    throw new GuidePublicError(0, null, fallback);
  }
};

const guide = (token: string) => `/stay-links/${encodeURIComponent(token)}/guide`;

/** The guide in whatever state the server says (pin_required … open). */
export const fetchGuestGuide = (token: string): Promise<GuestGuideBody> =>
  call(publicApi.get<GuestGuideBody>(guide(token)), 'Could not load your guide.');

/** Send the guide PIN. 200 → the browser now holds the unlock cookie; re-GET. */
export const postGuidePin = (token: string, pin: string): Promise<GuidePinOk> =>
  call(publicApi.post<GuidePinOk>(`${guide(token)}/pin`, { pin }), 'Could not check the PIN.');

/** One photo's bytes (the Share sheet needs a File, not a URL). */
export const fetchGuideMediaFile = (token: string, mediaId: string): Promise<Blob> =>
  call(
    publicApi.get<Blob>(`${guide(token)}/media/${encodeURIComponent(mediaId)}/file`, { responseType: 'blob' }),
    'Could not load the photo.'
  );

/* ── staff: the host preview (stays:manage) ── */

const wrapStaff = async <T>(p: Promise<{ data: T }>, fallback: string): Promise<T> => {
  try {
    const { data } = await p;

    return data;
  } catch (e) {
    const err = e as AxiosError<Record<string, unknown>>;
    if (err && err.response) throw new StaysApiError(err.response.status, err.response.data || null, fallback);
    throw new StaysApiError(0, null, fallback);
  }
};

/** The guest's open body as a guest at `stage` would see it (preview: true). */
export const fetchGuidePreview = (listingId: string, stage: GuidePreviewStage): Promise<GuideOpenBody> =>
  wrapStaff(
    axios.get<GuideOpenBody>(`${apiRoute}/stays/listings/${encodeURIComponent(listingId)}/guide/preview`, {
      params: { stage },
    }),
    'Could not load the preview.'
  );
